import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { slugify, toArray } from '../utils/slugify';
import { apiFetch } from '../api/apiClient';
import CustomSelect from '../components/ui/CustomSelect';

const DEFAULT_COLORS = [
  '#f472b6', '#c084fc', '#60a5fa', '#14b8a6', '#fde047', '#fb7185',
  '#818cf8', '#e879f9', '#a78bfa', '#2dd4bf', '#f9a8d4', '#93c5fd',
];

const ASSET_TYPE_LABEL = {
  Stock: 'Stocks', ETF: 'ETFs', Crypto: 'Crypto', Bond: 'Bonds',
  Commodity: 'Commodities', Fund: 'Funds',
  0: 'Stocks', 1: 'ETFs', 2: 'Crypto', 3: 'Bonds', 4: 'Commodities', 5: 'Funds',
};

const CURRENCY_REGION = {
  USD: 'USD · United States', EUR: 'EUR · Europe', GBP: 'GBP · United Kingdom',
  CZK: 'CZK · Czech Republic', JPY: 'JPY · Japan', CAD: 'CAD · Canada',
  AUD: 'AUD · Australia', CHF: 'CHF · Switzerland', PLN: 'PLN · Poland',
  SEK: 'SEK · Sweden', NOK: 'NOK · Norway', DKK: 'DKK · Denmark',
  HKD: 'HKD · Hong Kong', SGD: 'SGD · Singapore', CNY: 'CNY · China',
  KRW: 'KRW · South Korea', BRL: 'BRL · Brazil', INR: 'INR · India',
};

const COLOR_TABS = [
  { id: 'holdings',  label: 'Holdings'   },
  { id: 'currency',  label: 'Currency'   },
  { id: 'assetType', label: 'Asset Type' },
  { id: 'lines',     label: 'Chart Lines' },
];

const LINE_DEFAULTS = { total: '#8b7cff', invested: '#f59e0b', benchmark: '#06b6d4' };
const LINE_LABELS   = { total: 'Total Value', invested: 'Invested', benchmark: 'Benchmark' };

function PortfolioSettingsPage() {
  // Note: previously read user/isFree here to gate benchmark + wallet — those
  // gates are now removed (no public Free/Paid tier yet).
  const { slug: slugParam } = useParams();
  const location = useLocation();
  const slug = slugParam || location.pathname.replace(/^\/portfolio\//, '').split('/')[0] || '';
  const navigate = useNavigate();

  const [portfolioId, setPortfolioId] = useState(null);
  const [resolving, setResolving] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showWalletTooltip, setShowWalletTooltip] = useState(false);
  const [benchmarkSuggestions, setBenchmarkSuggestions] = useState([]);
  const [benchmarkSuggestionsLoading, setBenchmarkSuggestionsLoading] = useState(false);
  const benchmarkSearchTimeoutRef = useRef(null);

  // ── Color customisation (stored in localStorage, not on server) ──
  const [summaryHoldings, setSummaryHoldings] = useState([]);
  const [colorTab, setColorTab] = useState('holdings');
  const [assetColors, setAssetColors] = useState({});
  const [currencyColors, setCurrencyColors] = useState({});
  const [assetTypeColors, setAssetTypeColors] = useState({});
  const [chartLineColors, setChartLineColors] = useState({});

  const [form, setForm] = useState({
    name: '',
    baseCurrency: 'USD',
    benchmarkTicker: 'SPY',
    defaultChartRange: '180d',
    walletEnabled: false,
    walletMode: 'Automatic',
    includeCashInPerformance: true,
    includeDividendsInReturn: true,
    returnCalculationMethod: 'TWR',
    dividendMode: 'Cash',
    costBasisMethod: 'Average',
  });

  // Resolve slug → portfolio ID
  useEffect(() => {
    let isMounted = true;
    const resolve = async () => {
      try {
        setResolving(true);
        const res = await apiFetch('/api/portfolios');
        if (!res.ok) throw new Error('Failed to load portfolios.');
        const data = await res.json();
        const list = toArray(data);
        const match =
          list.find((p) => slugify(p.name) === slug) ||
          list.find((p) => String(p.id) === String(slug)) ||
          null;
        if (!isMounted) return;
        if (match) {
          setPortfolioId(String(match.id));
          const correctSlug = slugify(match.name);
          if (correctSlug && correctSlug !== slug) {
            navigate('/portfolio/' + correctSlug + '/settings', { replace: true });
          }
        } else {
          setError('Portfolio not found.');
        }
      } catch (err) {
        if (isMounted) setError(err.message || 'Failed to resolve portfolio.');
      } finally {
        if (isMounted) setResolving(false);
      }
    };
    resolve();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Load settings
  useEffect(() => {
    if (!portfolioId) return;
    let isMounted = true;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await apiFetch(`/api/portfolios/${portfolioId}`);
        if (!res.ok) throw new Error('Failed to load portfolio settings.');
        const data = await res.json();
        if (!isMounted) return;
        setForm({
          name: data?.name || '',
          baseCurrency: data?.baseCurrency || 'USD',
          benchmarkTicker: data?.benchmarkTicker || 'SPY',
          defaultChartRange: normalizeChartRange(data?.defaultChartRange),
          walletEnabled: typeof data?.walletEnabled === 'boolean' ? data.walletEnabled : false,
          walletMode: data?.walletMode || 'Automatic',
          includeCashInPerformance: typeof data?.includeCashInPerformance === 'boolean' ? data.includeCashInPerformance : true,
          includeDividendsInReturn: typeof data?.includeDividendsInReturn === 'boolean' ? data.includeDividendsInReturn : true,
          returnCalculationMethod: data?.returnCalculationMethod || 'TWR',
          dividendMode: data?.dividendMode || 'Cash',
          costBasisMethod: data?.costBasisMethod || 'Average',
        });
      } catch (err) {
        if (isMounted) setError(err.message || 'Failed to load portfolio settings.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [portfolioId]);

  useEffect(() => {
    return () => {
      if (benchmarkSearchTimeoutRef.current) clearTimeout(benchmarkSearchTimeoutRef.current);
    };
  }, []);

  // Load saved colors + fetch holdings for color pickers
  useEffect(() => {
    if (!portfolioId) return;
    try {
      setAssetColors(JSON.parse(localStorage.getItem(`assetColors-${portfolioId}`) || '{}'));
      setCurrencyColors(JSON.parse(localStorage.getItem(`currencyColors-${portfolioId}`) || '{}'));
      setAssetTypeColors(JSON.parse(localStorage.getItem(`assetTypeColors-${portfolioId}`) || '{}'));
      setChartLineColors(JSON.parse(localStorage.getItem(`chartLineColors-${portfolioId}`) || '{}'));
    } catch {}
    let isMounted = true;
    apiFetch(`/api/portfolio-v2/summary?portfolioId=${portfolioId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!isMounted) return;
        const all = Array.isArray(data?.holdings) ? data.holdings : [];
        setSummaryHoldings(all.filter(h => Number(h?.openQuantity || 0) > 0));
      })
      .catch(() => {});
    return () => { isMounted = false; };
  }, [portfolioId]);

  // ── Color helpers ────────────────────────────────────────────────────────────
  const holdingColorItems = useMemo(() => {
    const seen = new Set();
    return summaryHoldings
      .map(h => String(h.ticker || '').toUpperCase())
      .filter(t => t && !seen.has(t) && seen.add(t))
      .map((ticker, i) => ({
        key: ticker,
        label: ticker,
        autoColor: DEFAULT_COLORS[i % DEFAULT_COLORS.length],
        savedColor: assetColors[ticker] || null,
      }));
  }, [summaryHoldings, assetColors]);

  const currencyColorItems = useMemo(() => {
    const seen = new Set();
    return summaryHoldings
      .map(h => String(h.currency || '').toUpperCase())
      .filter(c => c && !seen.has(c) && seen.add(c))
      .map((cur, i) => {
        const label = CURRENCY_REGION[cur] || cur;
        return { key: label, label, autoColor: DEFAULT_COLORS[i % DEFAULT_COLORS.length], savedColor: currencyColors[label] || null };
      });
  }, [summaryHoldings, currencyColors]);

  const assetTypeColorItems = useMemo(() => {
    const seen = new Set();
    return summaryHoldings
      .map(h => { const raw = h.assetType ?? ''; return ASSET_TYPE_LABEL[raw] || String(raw) || 'Other'; })
      .filter(t => t && !seen.has(t) && seen.add(t))
      .map((label, i) => ({ key: label, label, autoColor: DEFAULT_COLORS[i % DEFAULT_COLORS.length], savedColor: assetTypeColors[label] || null }));
  }, [summaryHoldings, assetTypeColors]);

  const activeColorItems = colorTab === 'holdings' ? holdingColorItems : colorTab === 'currency' ? currencyColorItems : colorTab === 'assetType' ? assetTypeColorItems : [];

  // Fixed items for the "lines" tab
  const lineColorItems = useMemo(() =>
    Object.keys(LINE_DEFAULTS).map(key => ({
      key,
      label: LINE_LABELS[key],
      autoColor: LINE_DEFAULTS[key],
      savedColor: chartLineColors[key] || null,
    }))
  , [chartLineColors]);

  const saveColor = (tab, key, color) => {
    if (tab === 'holdings') {
      const next = { ...assetColors, [key]: color };
      setAssetColors(next);
      try { localStorage.setItem(`assetColors-${portfolioId}`, JSON.stringify(next)); } catch {}
    } else if (tab === 'currency') {
      const next = { ...currencyColors, [key]: color };
      setCurrencyColors(next);
      try { localStorage.setItem(`currencyColors-${portfolioId}`, JSON.stringify(next)); } catch {}
    } else if (tab === 'assetType') {
      const next = { ...assetTypeColors, [key]: color };
      setAssetTypeColors(next);
      try { localStorage.setItem(`assetTypeColors-${portfolioId}`, JSON.stringify(next)); } catch {}
    } else if (tab === 'lines') {
      const next = { ...chartLineColors, [key]: color };
      setChartLineColors(next);
      try { localStorage.setItem(`chartLineColors-${portfolioId}`, JSON.stringify(next)); } catch {}
    }
  };

  const resetColor = (tab, key) => {
    if (tab === 'holdings') {
      const next = { ...assetColors }; delete next[key];
      setAssetColors(next);
      try { localStorage.setItem(`assetColors-${portfolioId}`, JSON.stringify(next)); } catch {}
    } else if (tab === 'currency') {
      const next = { ...currencyColors }; delete next[key];
      setCurrencyColors(next);
      try { localStorage.setItem(`currencyColors-${portfolioId}`, JSON.stringify(next)); } catch {}
    } else if (tab === 'assetType') {
      const next = { ...assetTypeColors }; delete next[key];
      setAssetTypeColors(next);
      try { localStorage.setItem(`assetTypeColors-${portfolioId}`, JSON.stringify(next)); } catch {}
    } else if (tab === 'lines') {
      const next = { ...chartLineColors }; delete next[key];
      setChartLineColors(next);
      try { localStorage.setItem(`chartLineColors-${portfolioId}`, JSON.stringify(next)); } catch {}
    }
  };
  // ─────────────────────────────────────────────────────────────────────────────

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const searchBenchmarkSuggestions = async (query) => {
    if (!query || query.trim().length < 1) { setBenchmarkSuggestions([]); return; }
    try {
      setBenchmarkSuggestionsLoading(true);
      const res = await apiFetch(`/api/stock/search?query=${encodeURIComponent(query.trim())}`);
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      setBenchmarkSuggestions(Array.isArray(data) ? data.slice(0, 8) : []);
    } catch {
      setBenchmarkSuggestions([]);
    } finally {
      setBenchmarkSuggestionsLoading(false);
    }
  };

  const handleBenchmarkTickerChange = (value) => {
    handleChange('benchmarkTicker', value);
    if (benchmarkSearchTimeoutRef.current) clearTimeout(benchmarkSearchTimeoutRef.current);
    benchmarkSearchTimeoutRef.current = setTimeout(() => searchBenchmarkSuggestions(value), 250);
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');
      const payload = {
        name: form.name.trim() || 'Portfolio',
        baseCurrency: String(form.baseCurrency || 'USD').toUpperCase(),
        benchmarkTicker: form.benchmarkTicker.trim() ? form.benchmarkTicker.trim().toUpperCase() : null,
        defaultChartRange: normalizeChartRange(form.defaultChartRange),
        walletEnabled: Boolean(form.walletEnabled),
        walletMode: form.walletEnabled ? normalizeWalletMode(form.walletMode) : 'Automatic',
        includeCashInPerformance: Boolean(form.includeCashInPerformance),
        includeDividendsInReturn: Boolean(form.includeDividendsInReturn),
        returnCalculationMethod: form.returnCalculationMethod || 'TWR',
        dividendMode: form.dividendMode || 'Cash',
        costBasisMethod: form.costBasisMethod || 'Average',
      };
      const res = await apiFetch(`/api/portfolios/${portfolioId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to save portfolio settings.');
      }
      const updated = await res.json();
      const newSlug = slugify(updated?.name || '') || slug;
      navigate('/portfolio/' + newSlug);
    } catch (err) {
      setError(err.message || 'Failed to save portfolio settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePortfolio = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this portfolio? This will also delete all transactions inside it.'
    );
    if (!confirmed) return;
    try {
      setDeleting(true);
      setError('');
      const res = await apiFetch(`/api/portfolios/${portfolioId}`, { method: 'DELETE' });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to delete portfolio.');
      }
      navigate('/portfolios');
    } catch (err) {
      setError(err.message || 'Failed to delete portfolio.');
    } finally {
      setDeleting(false);
    }
  };

  if (resolving) {
    return <div style={pageStyle}><p style={mutedStyle}>Loading...</p></div>;
  }

  return (
    <div style={pageStyle}>
      <h1 style={pageTitleStyle}>Portfolio Settings</h1>
      <p style={pageSubtitleStyle}>
        Manage portfolio behavior, wallet rules, and default dashboard options.
      </p>

      {error && <div style={errorBoxStyle}>{error}</div>}
      {successMessage && <div style={successBoxStyle}>{successMessage}</div>}

      {loading ? (
        <p style={mutedStyle}>Loading settings...</p>
      ) : (
        <form onSubmit={handleSaveSettings} style={formStyle}>

          {/* ── General ─────────────────────────────────── */}
          <section style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <h2 style={sectionTitleStyle}>General</h2>
              <p style={sectionSubtitleStyle}>Core portfolio identity and default chart behavior.</p>
            </div>

            <div style={fourColGridStyle} className="rp-settings-4col">
              {/* Portfolio name */}
              <div style={fieldStyle}>
                <span style={labelStyle}>Portfolio name</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  style={inputStyle}
                />
              </div>

              {/* Base currency */}
              <div style={fieldStyle}>
                <span style={labelStyle}>Base currency</span>
                <CustomSelect
                  value={form.baseCurrency}
                  onChange={(v) => handleChange('baseCurrency', v)}
                  options={[
                    { value: 'USD', label: 'USD' },
                    { value: 'EUR', label: 'EUR' },
                    { value: 'CZK', label: 'CZK' },
                    { value: 'GBP', label: 'GBP' },
                    { value: 'PLN', label: 'PLN' },
                    { value: 'CHF', label: 'CHF' },
                  ]}
                />
              </div>

              {/* Default chart range */}
              <div style={fieldStyle}>
                <span style={labelStyle}>Default chart range</span>
                <CustomSelect
                  value={form.defaultChartRange}
                  onChange={(v) => handleChange('defaultChartRange', v)}
                  options={[
                    { value: '1d',   label: '1D' },
                    { value: '7d',   label: '7D' },
                    { value: '30d',  label: '1M' },
                    { value: '90d',  label: '3M' },
                    { value: '180d', label: '6M' },
                    { value: 'ytd',  label: 'YTD' },
                    { value: '1y',   label: '1Y' },
                    { value: '3y',   label: '3Y' },
                    { value: '5y',   label: '5Y' },
                    { value: 'all',  label: 'ALL' },
                  ]}
                />
              </div>

              {/* Benchmark ticker */}
              <div style={fieldStyle}>
                <span style={labelStyle}>Benchmark ticker</span>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={form.benchmarkTicker}
                    onChange={(e) => handleBenchmarkTickerChange(e.target.value)}
                    onBlur={() => setTimeout(() => setBenchmarkSuggestions([]), 200)}
                    placeholder="e.g. SPY, QQQ, MSFT"
                    style={inputStyle}
                    autoComplete="off"
                  />
                  {(benchmarkSuggestions.length > 0 || benchmarkSuggestionsLoading) && (
                    <div style={suggestionsDropdownStyle}>
                      {benchmarkSuggestionsLoading ? (
                        <div style={suggestionLoadingStyle}>Searching...</div>
                      ) : (
                        benchmarkSuggestions.map((item) => (
                          <button
                            key={item.symbol}
                            type="button"
                            onMouseDown={() => {
                              handleChange('benchmarkTicker', (item.symbol || '').toUpperCase());
                              setBenchmarkSuggestions([]);
                            }}
                            style={suggestionItemStyle}
                          >
                            <span style={suggestionTickerStyle}>{item.symbol}</span>
                            <span style={suggestionNameStyle}>
                              {item.name || item.shortname || ''}
                              {item.exchange ? ` · ${item.exchange}` : ''}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <span style={hintStyle}>Ticker for portfolio performance comparison.</span>
              </div>
            </div>
          </section>

          {/* ── Wallet ── */}
          <section style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <h2 style={sectionTitleStyle}>Wallet</h2>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>
                Wallet
                <span
                  style={tooltipWrapperStyle}
                  onMouseEnter={() => setShowWalletTooltip(true)}
                  onMouseLeave={() => setShowWalletTooltip(false)}
                >
                  <span style={tooltipTriggerStyle}>?</span>
                  {showWalletTooltip && (
                    <span style={tooltipBoxStyle}>
                      <strong>Off</strong> — add positions freely without cash restrictions.<br /><br />
                      <strong>On</strong> — cash balance is required before buying.
                    </span>
                  )}
                </span>
              </span>
              <CustomSelect
                value={form.walletEnabled ? 'true' : 'false'}
                onChange={(v) => handleChange('walletEnabled', v === 'true')}
                options={[
                  { value: 'false', label: 'Off' },
                  { value: 'true',  label: 'On' },
                ]}
              />
            </div>
          </section>

          {/* ── Performance & Calculation ────────────────── */}
          <section style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <h2 style={sectionTitleStyle}>Performance &amp; Calculation</h2>
              <p style={sectionSubtitleStyle}>Fine-tune how portfolio returns and cost basis are interpreted.</p>
            </div>
            <div style={fiveColGridStyle}>
              <div style={fieldStyle}>
                <span style={labelStyle}>Return calculation</span>
                <CustomSelect
                  value={form.returnCalculationMethod}
                  onChange={(v) => handleChange('returnCalculationMethod', v)}
                  options={[
                    { value: 'TWR', label: 'TWR' },
                    { value: 'MWR', label: 'MWR' },
                  ]}
                />
              </div>
              <div style={fieldStyle}>
                <span style={labelStyle}>Dividend mode</span>
                <CustomSelect
                  value={form.dividendMode}
                  onChange={(v) => handleChange('dividendMode', v)}
                  options={[
                    { value: 'Cash',     label: 'Cash' },
                    { value: 'Reinvest', label: 'Reinvest' },
                  ]}
                />
              </div>
              <div style={fieldStyle}>
                <span style={labelStyle}>Cost basis method</span>
                <CustomSelect
                  value={form.costBasisMethod}
                  onChange={(v) => handleChange('costBasisMethod', v)}
                  options={[
                    { value: 'Average', label: 'Average' },
                    { value: 'FIFO',    label: 'FIFO' },
                  ]}
                />
              </div>
              {form.walletEnabled && (
              <div style={fieldStyle}>
                <span style={labelStyle}>Include cash in performance</span>
                <CustomSelect
                  value={form.includeCashInPerformance ? 'true' : 'false'}
                  onChange={(v) => handleChange('includeCashInPerformance', v === 'true')}
                  options={[
                    { value: 'true',  label: 'Yes' },
                    { value: 'false', label: 'No' },
                  ]}
                />
              </div>
              )}
              <div style={fieldStyle}>
                <span style={labelStyle}>Include dividends in return</span>
                <CustomSelect
                  value={form.includeDividendsInReturn ? 'true' : 'false'}
                  onChange={(v) => handleChange('includeDividendsInReturn', v === 'true')}
                  options={[
                    { value: 'true',  label: 'Yes' },
                    { value: 'false', label: 'No' },
                  ]}
                />
              </div>
            </div>
          </section>

          {/* ── Chart Colors ────────────────────────────── */}
          <section style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <h2 style={sectionTitleStyle}>Chart Colors</h2>
              <p style={sectionSubtitleStyle}>
                Customize colors in the Portfolio Allocation chart. Changes save automatically — no need to click Save settings.
              </p>
            </div>

            <div style={colorTabBarStyle}>
              {COLOR_TABS.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setColorTab(tab.id)}
                  style={{ ...colorTabBtnStyle, ...(colorTab === tab.id ? colorTabBtnActiveStyle : {}) }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {colorTab === 'lines' ? (
              <div style={colorGridStyle}>
                {lineColorItems.map((item) => {
                  const effectiveColor = item.savedColor || item.autoColor;
                  const isCustom = Boolean(item.savedColor);
                  return (
                    <div key={item.key} style={colorRowStyle}>
                      <div style={{ position: 'relative', flexShrink: 0 }} title="Click to change color">
                        <div style={{
                          width: '32px', height: '32px', borderRadius: '8px',
                          background: effectiveColor,
                          outline: isCustom ? '2px solid rgba(255,255,255,0.45)' : 'none',
                          outlineOffset: '1px',
                        }} />
                        <input
                          type="color"
                          value={effectiveColor}
                          onChange={e => saveColor('lines', item.key, e.target.value)}
                          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%', border: 'none', padding: 0 }}
                        />
                      </div>
                      <span style={colorItemLabelStyle}>{item.label}</span>
                      {isCustom ? (
                        <button type="button" onClick={() => resetColor('lines', item.key)} style={colorResetBtnStyle}>Reset</button>
                      ) : (
                        <span style={colorAutoTagStyle}>default</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : activeColorItems.length === 0 ? (
              <p style={mutedStyle}>
                {summaryHoldings.length === 0 ? 'Loading holdings…' : `No ${colorTab === 'holdings' ? 'holdings' : colorTab === 'currency' ? 'currencies' : 'asset types'} found.`}
              </p>
            ) : (
              <div style={colorGridStyle}>
                {activeColorItems.map((item) => {
                  const effectiveColor = item.savedColor || item.autoColor;
                  const isCustom = Boolean(item.savedColor);
                  return (
                    <div key={item.key} style={colorRowStyle}>
                      {/* Swatch + invisible color input */}
                      <div style={{ position: 'relative', flexShrink: 0 }} title="Click to change color">
                        <div style={{
                          width: '32px', height: '32px', borderRadius: '8px',
                          background: effectiveColor,
                          outline: isCustom ? '2px solid rgba(255,255,255,0.45)' : 'none',
                          outlineOffset: '1px',
                        }} />
                        <input
                          type="color"
                          value={effectiveColor}
                          onChange={e => saveColor(colorTab, item.key, e.target.value)}
                          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%', border: 'none', padding: 0 }}
                        />
                      </div>

                      <span style={colorItemLabelStyle}>{item.label}</span>

                      {isCustom ? (
                        <button
                          type="button"
                          onClick={() => resetColor(colorTab, item.key)}
                          style={colorResetBtnStyle}
                        >
                          Reset
                        </button>
                      ) : (
                        <span style={colorAutoTagStyle}>auto</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* ── Save button ─────────────────────────────── */}
          <div style={actionsStyle}>
            <button type="submit" style={saveButtonStyle} disabled={saving || deleting}>
              {saving ? 'Saving...' : 'Save settings'}
            </button>
          </div>

          {/* ── Danger zone ─────────────────────────────── */}
          <section style={dangerSectionStyle}>
            <div style={dangerHeaderStyle}>
              <div>
                <h3 style={dangerTitleStyle}>Danger Zone</h3>
                <p style={dangerTextStyle}>
                  Deleting a portfolio removes it together with all its transactions.
                </p>
              </div>
              <button
                type="button"
                onClick={handleDeletePortfolio}
                style={deleteButtonStyle}
                disabled={saving || deleting}
              >
                {deleting ? 'Deleting...' : 'Delete portfolio'}
              </button>
            </div>
          </section>
        </form>
      )}
    </div>
  );
}

function normalizeWalletMode(m) {
  return String(m || '').toLowerCase() === 'manual' ? 'Manual' : 'Automatic';
}

function normalizeChartRange(value) {
  const v = String(value || '180d').trim().toLowerCase();
  switch (v) {
    case '1d': case '7d': case '30d': case '90d': case '180d':
    case 'ytd': case '1y': case '3y': case '5y': case 'all':
      return v;
    case '1mo': return '30d';
    case '3mo': return '90d';
    case '6mo': return '180d';
    default: return '180d';
  }
}

/* ── Styles ── */
const pageStyle = {
  maxWidth: '1100px',
  width: '100%',
  margin: '0 auto',
  padding: '20px 0 60px',
  boxSizing: 'border-box',
  display: 'grid',
  gap: '24px',
  alignContent: 'start',
};


const pageTitleStyle = {
  margin: 0,
  color: 'var(--text-primary)',
  fontSize: '28px',
  fontWeight: 800,
};

const pageSubtitleStyle = {
  margin: 0,
  color: 'var(--text-secondary)',
  lineHeight: 1.5,
};

const formStyle = {
  display: 'grid',
  gap: '20px',
};

const sectionStyle = {
  background: 'var(--surface-strong)',
  borderRadius: '18px',
  padding: '24px',
  boxShadow: 'var(--shadow)',
  border: '1px solid var(--border)',
  display: 'grid',
  gap: '20px',
};

const sectionHeaderStyle = {
  display: 'grid',
  gap: '4px',
};

const sectionTitleStyle = {
  margin: 0,
  color: 'var(--text-primary)',
  fontSize: '18px',
  fontWeight: 800,
};

const sectionSubtitleStyle = {
  margin: 0,
  color: 'var(--text-secondary)',
  fontSize: '14px',
  lineHeight: 1.5,
};

/* 4 equal columns for General – collapse to 2 on narrow screens */
const fourColGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '16px',
  alignItems: 'start',
};

/* 5 equal columns for Performance */
const fiveColGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
  gap: '16px',
  alignItems: 'start',
};

const fieldStyle = {
  display: 'grid',
  gap: '6px',
};

const labelStyle = {
  fontSize: '12px',
  fontWeight: 700,
  color: 'var(--text-secondary)',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

const inputStyle = {
  width: '100%',
  minHeight: '48px',
  borderRadius: '12px',
  border: '1px solid var(--border)',
  background: 'var(--input-bg)',
  color: 'var(--text-primary)',
  padding: '12px 14px',
  fontSize: '14px',
  boxSizing: 'border-box',
};

const hintStyle = {
  color: 'var(--text-secondary)',
  fontSize: '12px',
  lineHeight: 1.45,
};

const actionsStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
};

const saveButtonStyle = {
  border: 'none',
  borderRadius: '12px',
  padding: '12px 32px',
  fontWeight: 700,
  fontSize: '15px',
  cursor: 'pointer',
  background: 'var(--primary)',
  color: '#ffffff',
};

const dangerSectionStyle = {
  borderRadius: '16px',
  padding: '20px 24px',
  border: '1px solid rgba(220, 38, 38, 0.35)',
  background: 'rgba(220, 38, 38, 0.08)',
};

const dangerHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '16px',
  flexWrap: 'wrap',
};

const dangerTitleStyle = {
  margin: 0,
  color: '#fca5a5',
  fontSize: '18px',
  fontWeight: 800,
};

const dangerTextStyle = {
  margin: '6px 0 0 0',
  color: '#fecaca',
  lineHeight: 1.5,
  fontSize: '14px',
};

const deleteButtonStyle = {
  border: 'none',
  borderRadius: '12px',
  padding: '12px 20px',
  fontWeight: 700,
  cursor: 'pointer',
  background: '#dc2626',
  color: '#ffffff',
  flexShrink: 0,
};

const errorBoxStyle = {
  padding: '12px 16px',
  borderRadius: '12px',
  background: 'rgba(220, 38, 38, 0.12)',
  color: '#fca5a5',
  border: '1px solid rgba(220, 38, 38, 0.35)',
  fontWeight: 600,
};

const successBoxStyle = {
  padding: '12px 16px',
  borderRadius: '12px',
  background: 'rgba(34, 197, 94, 0.12)',
  color: '#86efac',
  border: '1px solid rgba(34, 197, 94, 0.35)',
  fontWeight: 600,
};

const mutedStyle = {
  color: 'var(--text-secondary)',
  margin: 0,
};

const tooltipWrapperStyle = {
  position: 'relative',
  display: 'inline-block',
  marginLeft: '4px',
};

const tooltipTriggerStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  background: 'var(--input-bg)',
  border: '1px solid var(--border)',
  color: 'var(--text-secondary)',
  fontSize: '10px',
  fontWeight: 700,
  cursor: 'default',
  userSelect: 'none',
};

const tooltipBoxStyle = {
  position: 'absolute',
  bottom: 'calc(100% + 8px)',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '260px',
  padding: '12px 14px',
  borderRadius: '12px',
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
  boxShadow: 'var(--shadow)',
  color: 'var(--text-secondary)',
  fontSize: '12px',
  lineHeight: 1.6,
  zIndex: 100,
  pointerEvents: 'none',
};

const suggestionsDropdownStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  zIndex: 50,
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  boxShadow: 'var(--shadow)',
  overflow: 'hidden',
};

const suggestionLoadingStyle = {
  padding: '10px 14px',
  color: 'var(--text-secondary)',
  fontSize: '13px',
};

const suggestionItemStyle = {
  display: 'flex',
  alignItems: 'baseline',
  gap: '10px',
  width: '100%',
  padding: '10px 14px',
  background: 'none',
  border: 'none',
  borderBottom: '1px solid var(--border)',
  cursor: 'pointer',
  textAlign: 'left',
  color: 'var(--text-primary)',
};

const suggestionTickerStyle = {
  fontWeight: 800,
  fontSize: '14px',
  minWidth: '56px',
};

const suggestionNameStyle = {
  fontSize: '12px',
  color: 'var(--text-secondary)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/* ── Color section styles ── */
const colorTabBarStyle = {
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
};

const colorTabBtnStyle = {
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-primary)',
  borderRadius: '999px',
  padding: '7px 14px',
  fontSize: '13px',
  fontWeight: 700,
  cursor: 'pointer',
};

const colorTabBtnActiveStyle = {
  background: 'var(--primary)',
  color: '#ffffff',
  border: '1px solid var(--primary)',
};

const colorGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: '10px',
};

const colorRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '10px 14px',
  background: 'var(--surface)',
  borderRadius: '12px',
  border: '1px solid var(--border)',
};

const colorItemLabelStyle = {
  flex: 1,
  fontSize: '14px',
  fontWeight: 700,
  color: 'var(--text-primary)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const colorResetBtnStyle = {
  border: '1px solid var(--border)',
  background: 'none',
  color: 'var(--text-secondary)',
  borderRadius: '8px',
  padding: '4px 10px',
  fontSize: '12px',
  fontWeight: 700,
  cursor: 'pointer',
  flexShrink: 0,
};

const colorAutoTagStyle = {
  fontSize: '11px',
  fontWeight: 700,
  color: 'var(--text-secondary)',
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
  borderRadius: '6px',
  padding: '2px 7px',
  flexShrink: 0,
};

export default PortfolioSettingsPage;
